// Phân bổ số giá trị hiện của từng nhóm searchCategories theo độ rộng thật của khung:
// - Mỗi nhóm bắt đầu với vài giá trị, tính số dòng tối thiểu khi xếp flex-wrap.
// - Lần lượt (vòng tròn cho đều) cho từng nhóm thêm 1 giá trị, chỉ nhận nếu không làm
//   tăng số dòng. Chỉ 1 nhóm → lấp đầy 1 dòng; nhiều nhóm → chia đều, nhóm ở cuối dòng
//   lấp nốt chỗ trống của dòng đó.
// - Giá trị đang chọn luôn hiện; nhóm người dùng đã mở rộng hiện hết.

export const DEFAULT_MIN_PER_GROUP = 3;
const DEFAULT_GAP = 5;
const DEFAULT_ITEM_OVERHEAD = 16;
const DEFAULT_GROUP_CHROME = 14;
const DEFAULT_TOGGLE_WIDTH = 34;

// Độ rộng một nhóm khi hiện `quota` giá trị đầu (+ giá trị đang chọn nằm sau quota).
export const groupWidth = (group, quota, metrics) => {
    const widths = group.widths || [];
    const selected = group.selected || [];
    let total = group.chrome !== undefined ? group.chrome : metrics.groupChrome;
    let hidden = 0;
    for (let i = 0; i < widths.length; i++) {
        if (i < quota || selected[i]) total += widths[i] + metrics.itemOverhead;
        else hidden++;
    }
    if (hidden > 0 || group.expandedToggle) total += metrics.toggleWidth;
    return total;
};

// Xếp các nhóm theo flex-wrap trong khung rộng `width`: dòng bắt đầu của từng nhóm + tổng số dòng.
export const assignRows = (groups, quotas, metrics) => {
    const width = metrics.width;
    const starts = [];
    let rows = 0;
    let used = 0;
    groups.forEach((group, index) => {
        const w = groupWidth(group, quotas[index], metrics);
        if (w > width) {
            // Nhóm rộng hơn khung tự xuống dòng bên trong, chiếm trọn các dòng riêng.
            starts.push(rows + (used > 0 ? 1 : 0));
            rows += (used > 0 ? 1 : 0) + Math.ceil(w / width);
            used = 0;
            return;
        }
        if (used > 0 && used + metrics.gap + w <= width) {
            starts.push(rows - 1);
            used += metrics.gap + w;
            return;
        }
        starts.push(rows);
        rows += 1;
        used = w;
    });
    return { rows, starts };
};

export const countRows = (groups, quotas, metrics) => assignRows(groups, quotas, metrics).rows;

export const planCategoryQuotas = (groups, options = {}) => {
    const metrics = {
        width: options.width || 0,
        gap: options.gap !== undefined ? options.gap : DEFAULT_GAP,
        itemOverhead: options.itemOverhead !== undefined ? options.itemOverhead : DEFAULT_ITEM_OVERHEAD,
        groupChrome: options.groupChrome !== undefined ? options.groupChrome : DEFAULT_GROUP_CHROME,
        toggleWidth: options.toggleWidth !== undefined ? options.toggleWidth : DEFAULT_TOGGLE_WIDTH
    };
    const minPerGroup = options.minPerGroup !== undefined ? options.minPerGroup : DEFAULT_MIN_PER_GROUP;
    const counts = groups.map((g) => (g.widths ? g.widths.length : 0));
    const fixed = groups.map((g, i) => g.expanded || g.fixedQuota !== undefined || counts[i] === 0);
    const quotas = groups.map((g, i) =>
        g.expanded ? counts[i] : g.fixedQuota !== undefined ? g.fixedQuota : Math.min(counts[i], minPerGroup)
    );
    if (!(metrics.width > 0)) return quotas;

    // Chỉ nhận thêm giá trị khi không tăng số dòng VÀ không đẩy nhóm nào sang dòng khác —
    // tránh nhóm đầu "ăn" cả dòng rồi dồn các nhóm sau xuống.
    const base = assignRows(groups, quotas, metrics);
    const sameLayout = (next) => next.rows <= base.rows && next.starts.every((row, i) => row === base.starts[i]);
    let grew = true;
    while (grew) {
        grew = false;
        for (let i = 0; i < groups.length; i++) {
            if (fixed[i] || quotas[i] >= counts[i]) continue;
            quotas[i] += 1;
            if (sameLayout(assignRows(groups, quotas, metrics))) grew = true;
            else quotas[i] -= 1;
        }
    }
    return quotas;
};

let measureCanvas = null;
const measureText = (text, font) => {
    if (typeof document === 'undefined') return String(text || '').length * 7;
    if (!measureCanvas) measureCanvas = document.createElement('canvas');
    const context = measureCanvas.getContext && measureCanvas.getContext('2d');
    if (!context) return String(text || '').length * 7;
    context.font = font;
    return context.measureText(String(text || '')).width;
};

const outerWidth = (element) => {
    if (!element) return 0;
    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);
    return rect.width + (parseFloat(style.marginLeft) || 0) + (parseFloat(style.marginRight) || 0);
};

// Bộ điều phối dùng chung cho các AntGridCategories trong một AntGrid. Không dùng state
// của AntGrid (AntGrid render lại sẽ làm DataGrid mất dòng đang chọn).
export default class AntGridCategoryLayout {
    constructor() {
        this.groups = new Map();
        this.listeners = new Map();
        this.quotas = new Map();
        this.element = null;
        this.observer = null;
        this.frame = null;
        this.calibrations = 0;
        this.metrics = {};
    }

    attach(element) {
        if (element === this.element) return;
        if (this.observer) this.observer.disconnect();
        this.element = element;
        if (element && typeof ResizeObserver !== 'undefined') {
            this.observer = new ResizeObserver(() => this.schedule(true));
            this.observer.observe(element);
        }
        this.schedule(true);
    }

    register(key, info, listener) {
        this.groups.set(key, info);
        this.listeners.set(key, listener);
        this.schedule(true);
    }

    unregister(key) {
        this.groups.delete(key);
        this.listeners.delete(key);
        this.quotas.delete(key);
        this.schedule(true);
    }

    // AntGrid unmount (hoặc StrictMode chạy lại effect): ngừng theo dõi, giữ nhóm đã đăng ký.
    suspend() {
        if (this.observer) this.observer.disconnect();
        this.observer = null;
        if (this.frame) {
            if (typeof cancelAnimationFrame !== 'undefined') cancelAnimationFrame(this.frame);
            else clearTimeout(this.frame);
        }
        this.frame = null;
    }

    resume() {
        if (this.element && !this.observer && typeof ResizeObserver !== 'undefined') {
            this.observer = new ResizeObserver(() => this.schedule(true));
            this.observer.observe(this.element);
        }
        this.schedule(true);
    }

    schedule(resetCalibration) {
        if (resetCalibration) this.calibrations = 0;
        if (this.frame) return;
        const run = () => {
            this.frame = null;
            this.compute();
        };
        this.frame = typeof requestAnimationFrame !== 'undefined' ? requestAnimationFrame(run) : setTimeout(run, 0);
    }

    // Đo phần đệm thật của nút/nhóm/nút "..." từ DOM đang hiển thị.
    calibrate() {
        const element = this.element;
        const metrics = { ...this.metrics };
        if (!element) return metrics;
        const text = element.querySelector('.ant-grid-categories .dx-buttongroup-item:not(.ant-grid-categories-toggle) .dx-button-text');
        if (text) {
            const style = window.getComputedStyle(text);
            metrics.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
            const item = text.closest('.dx-buttongroup-item');
            const measured = measureText(String(text.textContent || '').toUpperCase(), metrics.font);
            const overhead = outerWidth(item) - measured;
            if (overhead > 0 && overhead < 60) metrics.itemOverhead = overhead;
        }
        const toggle = element.querySelector('.ant-grid-categories .ant-grid-categories-toggle');
        if (toggle) metrics.toggleWidth = outerWidth(toggle);
        const toolbar = element.querySelector('.ant-grid-categories');
        const buttonGroup = toolbar && toolbar.querySelector('.dx-buttongroup');
        if (toolbar && buttonGroup) {
            const chrome = toolbar.getBoundingClientRect().width - buttonGroup.getBoundingClientRect().width;
            if (chrome >= 0 && chrome < 80) metrics.groupChrome = chrome;
        }
        const style = window.getComputedStyle(element);
        const gap = parseFloat(style.columnGap || style.gap);
        if (!Number.isNaN(gap)) metrics.gap = gap;
        return metrics;
    }

    compute() {
        const element = this.element;
        if (!element) return;
        const width = element.clientWidth;
        if (!(width > 0)) return;

        const previous = this.metrics;
        this.metrics = this.calibrate();
        const font = this.metrics.font || '500 12px sans-serif';

        const keys = [];
        const groups = [];
        this.groups.forEach((info, key) => {
            if (!info || info.visible === false || !(info.texts instanceof Array) || info.texts.length === 0) return;
            keys.push(key);
            groups.push({
                widths: info.texts.map((t) => measureText(String(t).toUpperCase(), font)),
                selected: info.selected || [],
                expanded: Boolean(info.expanded),
                fixedQuota: info.fixedQuota
            });
        });
        // Giữ thứ tự hiển thị (searchCategories1..10).
        const order = keys.map((key, index) => ({ key, index })).sort((a, b) => a.key - b.key);
        const orderedGroups = order.map((o) => groups[o.index]);
        const options = { ...this.metrics, width };
        // Lượt 1: coi mọi nhóm đang thu gọn → nhóm nào thu gọn mà vẫn giấu bớt giá trị thì
        // mới cần nút thu gọn khi đang mở rộng.
        const collapsedQuotas = planCategoryQuotas(orderedGroups.map((g) => ({ ...g, expanded: false })), options);
        const collapsible = orderedGroups.map((g, i) => collapsedQuotas[i] < g.widths.length);
        const quotas = planCategoryQuotas(
            orderedGroups.map((g, i) => ({ ...g, expandedToggle: g.expanded && collapsible[i] })),
            options
        );

        order.forEach((o, i) => {
            const next = { quota: quotas[i], collapsible: collapsible[i] };
            const current = this.quotas.get(o.key);
            if (current && current.quota === next.quota && current.collapsible === next.collapsible) return;
            this.quotas.set(o.key, next);
            const listener = this.listeners.get(o.key);
            if (listener) listener(next);
        });

        // Lần đầu dùng số đo mặc định; sau khi nút hiện ra thì đo lại cho khớp (tối đa 3 lượt).
        const changed = ['itemOverhead', 'toggleWidth', 'groupChrome', 'font'].some((k) => previous[k] !== this.metrics[k]);
        if (changed && this.calibrations < 3) {
            this.calibrations += 1;
            this.schedule(false);
        }
    }
}
